import React, { useState } from 'react';
import { Button } from '../../design-system/Button';
import { Input } from '../../design-system/Input';
import { Textarea } from '../../design-system/Textarea';
import { coachOnboardingAPI } from '../../../api/coachApi';
import { BasicInfoFormData, CoachType, COACH_TYPE_DISPLAY } from '../../../types/coach';

interface BasicInfoStepProps {
  onComplete: () => void;
  onSkip?: () => void;
  progress?: any;
}

const BasicInfoStep: React.FC<BasicInfoStepProps> = ({ onComplete }) => {
  const [formData, setFormData] = useState<BasicInfoFormData>({
    bio: '',
    specializations: [],
    secondary_types: [],
    languages_spoken: ['English'],
  });
  const [loading, setLoading] = useState(false);
  const [error, setError] = useState('');
  const [specializationInput, setSpecializationInput] = useState('');
  const [languageInput, setLanguageInput] = useState('');

  const handleSubmit = async (e: React.FormEvent) => {
    e.preventDefault();
    
    if (!formData.bio || formData.specializations.length === 0) {
      setError('Please fill in all required fields');
      return;
    }

    try {
      setLoading(true);
      setError('');
      await coachOnboardingAPI.updateBasicInfo(formData);
      onComplete();
    } catch (err: any) {
      setError(err.message || 'Failed to update information');
    } finally {
      setLoading(false);
    }
  };

  const addSpecialization = () => {
    if (specializationInput.trim() && !formData.specializations.includes(specializationInput.trim())) {
      setFormData({
        ...formData,
        specializations: [...formData.specializations, specializationInput.trim()],
      });
      setSpecializationInput('');
    }
  };

  const removeSpecialization = (spec: string) => {
    setFormData({
      ...formData,
      specializations: formData.specializations.filter(s => s !== spec),
    });
  };

  const addLanguage = () => {
    if (languageInput.trim() && !formData.languages_spoken?.includes(languageInput.trim())) {
      setFormData({
        ...formData,
        languages_spoken: [...(formData.languages_spoken || []), languageInput.trim()],
      });
      setLanguageInput('');
    }
  };

  const removeLanguage = (lang: string) => {
    setFormData({
      ...formData,
      languages_spoken: formData.languages_spoken?.filter(l => l !== lang) || [],
    });
  };

  const toggleSecondaryType = (type: CoachType) => {
    const currentTypes = formData.secondary_types || [];
    if (currentTypes.includes(type)) {
      setFormData({
        ...formData,
        secondary_types: currentTypes.filter(t => t !== type),
      });
    } else {
      setFormData({
        ...formData,
        secondary_types: [...currentTypes, type],
      });
    }
  };

  return (
    <form onSubmit={handleSubmit} className="space-y-6">
      {error && (
        <div className="bg-red-50 border border-red-200 rounded-lg p-4 text-red-700">
          {error}
        </div>
      )}

      {/* Bio */}
      <div>
        <label htmlFor="bio" className="block text-sm font-medium text-gray-700 mb-2">
          Professional Bio *
        </label>
        <Textarea
          id="bio"
          rows={4}
          placeholder="Tell potential clients about your coaching philosophy, approach, and what makes you unique..."
          value={formData.bio}
          onChange={(e) => setFormData({ ...formData, bio: e.target.value })}
          className="w-full"
        />
        <p className="mt-1 text-sm text-gray-500">
          Minimum 100 characters. This will be displayed on your public profile.
        </p>
      </div>

      {/* Specializations */}
      <div>
        <label className="block text-sm font-medium text-gray-700 mb-2">
          Specializations *
        </label>
        <div className="flex gap-2 mb-2">
          <Input
            type="text"
            placeholder="Add a specialization (e.g., weight loss, strength training)"
            value={specializationInput}
            onChange={(e) => setSpecializationInput(e.target.value)}
            onKeyPress={(e) => e.key === 'Enter' && (e.preventDefault(), addSpecialization())}
            className="flex-1"
          />
          <Button type="button" onClick={addSpecialization} size="sm">
            Add
          </Button>
        </div>
        <div className="flex flex-wrap gap-2">
          {formData.specializations.map((spec) => (
            <span
              key={spec}
              className="inline-flex items-center px-3 py-1 rounded-full text-sm bg-[#4B5FDC] text-white"
            >
              {spec}
              <button
                type="button"
                onClick={() => removeSpecialization(spec)}
                className="ml-2 hover:text-gray-200"
              >
                ×
              </button>
            </span>
          ))}
        </div>
      </div>

      {/* Secondary Coach Types */}
      <div>
        <label className="block text-sm font-medium text-gray-700 mb-2">
          Additional Coaching Areas (Optional)
        </label>
        <p className="text-sm text-gray-500 mb-3">
          Select any additional areas where you provide coaching services
        </p>
        <div className="grid grid-cols-2 md:grid-cols-3 gap-3">
          {Object.entries(COACH_TYPE_DISPLAY).map(([key, display]) => (
            <label
              key={key}
              className="flex items-center space-x-2 cursor-pointer"
            >
              <input
                type="checkbox"
                checked={formData.secondary_types?.includes(key as CoachType) || false}
                onChange={() => toggleSecondaryType(key as CoachType)}
                className="h-4 w-4 text-[#4B5FDC] focus:ring-[#4B5FDC] border-gray-300 rounded"
              />
              <span className="text-sm text-gray-700">{display}</span>
            </label>
          ))}
        </div>
      </div>

      {/* Languages */}
      <div>
        <label className="block text-sm font-medium text-gray-700 mb-2">
          Languages Spoken
        </label>
        <div className="flex gap-2 mb-2">
          <Input
            type="text"
            placeholder="Add a language"
            value={languageInput}
            onChange={(e) => setLanguageInput(e.target.value)}
            onKeyPress={(e) => e.key === 'Enter' && (e.preventDefault(), addLanguage())}
            className="flex-1"
          />
          <Button type="button" onClick={addLanguage} size="sm">
            Add
          </Button>
        </div>
        <div className="flex flex-wrap gap-2">
          {formData.languages_spoken?.map((lang) => (
            <span
              key={lang}
              className="inline-flex items-center px-3 py-1 rounded-full text-sm bg-gray-200 text-gray-700"
            >
              {lang}
              {lang !== 'English' && (
                <button
                  type="button"
                  onClick={() => removeLanguage(lang)}
                  className="ml-2 hover:text-gray-500"
                >
                  ×
                </button>
              )}
            </span>
          ))}
        </div>
      </div>

      {/* Submit Button */}
      <div className="flex justify-end pt-6">
        <Button type="submit" isLoading={loading} size="lg">
          Continue
        </Button>
      </div>
    </form>
  );
};

export default BasicInfoStep;